import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, CircularProgress} from 'views/components';
import backend from 'utils/backend';
import {BrokenImage as BrokenImageIcon} from 'views/icons';

const SecureImageDisplay = (props) => {
  const {
    images = [],
    sx = {},
  } = props;

  const [loadedImages, setLoadedImages] = React.useState([]);

  React.useEffect(() => {
    let isMounted = true;
    const fetchImages = async () => {
      if (!images || images.length === 0) {
        setLoadedImages([]);
        return;
      }

      const results = await Promise.all(
          images.map(async (img) => {
            // If this is a progress object, just return as is
            if ('progress' in img) {
              return img;
            }

            try {
              const file = await backend.fileStorage.getSecureFile(img.src);
              const fileSrc = URL.createObjectURL(file);
              return {...img, uri: fileSrc};
            } catch (e) {
              return {...img, error: true};
            }
          }),
      );

      if (isMounted) setLoadedImages(results);
    };

    fetchImages();

    return () => {
      isMounted = false;
      // Clean up object URLs to prevent memory leaks
      loadedImages.forEach((img) => {
        if (img.uri && img.uri.startsWith('blob:')) {
          URL.revokeObjectURL(img.uri);
        }
      });
    };
  }, [images]);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 1.5,
      alignItems: 'flex-start',
      marginBottom: 1,
      ...sx,
    }}>
      {
        loadedImages.map((img, idx) => {
          if (img.error) {
            return (
              <BrokenImageIcon key={idx} name='broken-image' size={32} />
            );
          } else if (!img.progress) {
            return (
              <Box
                key={idx}
                component="img"
                src={img.uri}
                alt=""
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: 1,
                  margin: 0.5,
                  objectFit: 'cover',
                }}
              />
            );
          } else {
            return (
              <Box
                key={idx}
                sx={{
                  width: 120,
                  height: 120,
                  border: 1,
                  borderColor: '#ddd',
                  borderRadius: 1,
                  padding: 1,
                  margin: 0.5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fafbfc',
                }}
              >
                <CircularProgress
                  size={32}
                  sx={{marginBottom: 1}}
                />
                <Typography sx={{fontSize: 12, color: 'text.secondary'}}>
                  {Math.round((img.progress || 0) * 100)}%
                </Typography>
              </Box>
            );
          }
        })
      }
    </Box>
  );
};

SecureImageDisplay.propTypes = {
  images: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape({
          src: PropTypes.string.isRequired,
          name: PropTypes.string,
          type: PropTypes.string,
        }),
        PropTypes.shape({
          name: PropTypes.string,
          progress: PropTypes.number.isRequired,
        }),
      ]),
  ).isRequired,
  sx: PropTypes.object,
};

export default SecureImageDisplay;
